import { RootState, VegobjekttypeCategoryState, VegobjekttypeState } from '@/state';
import {
    ActionType,
    AddVegobjektAction,
    VOClearAdvancedFilterAction,
    GetState,
    IsAddingCatToVOAction,
    RefreshAction,
    VOAddAction,
    VOAddCategoryAction,
    VOAllCategoryColorsChangedAction,
    VOCategoryColorChangedAction,
    VOColorChangedAction,
    VOUpdateAdvancedFilterAppliedAction,
    VOUpdateAdvancedFilterDraftAction,
    VORemoveAction,
    VORemoveCategoryAction,
    VOSetCategoryAction,
    VOToggleAction,
    VOToggleCategoryAction,
    RootAction,
} from './actiontypes';
import { Integration } from '@/middleware/integration';
import { Vegobjekttype } from '@/domain/vegobjekter/Vegobjekttype';
import { stateSelectedRoadObjectTypes, stateValgt } from '@/selectors/selectors';
import { updateSelectedColor } from './valgtActions';
import { ColorIndex, defaultColor, resolveColor } from '@/utils/ColorHelper';
import {
    addRecentCustomColor,
    clearDefaultCategoryColor,
    clearDefaultCategoryColors,
    clearDefaultVegobjekttypeColor,
    resolveInitialVegobjekttypeColor,
    setDefaultCategoryColor,
    setDefaultVegobjekttypeColor,
} from '@/utils/ColorPreferences';
import { Egenskapstype } from '@/domain/vegobjekter/Egenskapstype';
import { CommonQueries, Egenskapsfilter, OperatorE } from '@/middleware/queries';
import {
    executeAllSearches,
    fetchVegobjekt,
    fetchVegobjekter,
} from '@/middleware/fetchVegobjekter';
import { Vegobjekt } from '@/domain/vegobjekter/Vegobjekt';
import {
    type AdvancedFilterGroupNode,
    appendAdvancedFilterNode,
    createAdvancedFilterEgenskapNode,
    createAdvancedFilterRelasjonNode,
    migrateLegacyFiltersToAdvancedFilter,
    normalizeAdvancedFilterRoot,
    validateAdvancedFilter,
} from '@/domain/advancedFilter';
import type { ThunkDispatch } from 'redux-thunk';
import * as selectors from '../selectors/selectors';

type Dispatch = ThunkDispatch<
    RootState,
    Integration,
    RootAction | AddVegobjektAction | RefreshAction
>;

const typeIdOf = (votState: VegobjekttypeState): number => votState.typeId!;

export type addVegobjektToSearchT = (
    vegobjekt: Vegobjekt
) => (dispatch: Dispatch, getState: GetState) => void;
export const addVegobjektToSearch: addVegobjektToSearchT =
    (vegobjekt: Vegobjekt) => (dispatch: Dispatch, _: GetState) => {
        const addVegobjektAction: AddVegobjektAction = {
            type: ActionType.VEGOBJEKT_ADDED,
            vegobjekt,
        };
        dispatch(addVegobjektAction);
        dispatch(fetchVegobjekt(vegobjekt.id));
    };

export type refreshVegobjektSearchT = () => (dispatch: Dispatch) => void;
export const refreshVegobjektSearch: refreshVegobjektSearchT =
    () => (dispatch: Dispatch) => {
        const refreshAction: RefreshAction = {
            type: ActionType.TIMESTAMP_CHANGED,
        };
        dispatch(refreshAction);
        dispatch(executeAllSearches());
    };

export type addVegobjekttypeToSearchT = (
    vegobjekttype: Vegobjekttype,
    egenkapsId?: number,
    verdi?: number,
    relasjonsId?: number
) => (dispatch: Dispatch, getState: GetState, integration: Integration) => void;
export const addVegobjekttypeToSearch: addVegobjekttypeToSearchT =
    (
        vegobjekttypeRaw: Vegobjekttype,
        egenkapsId?: number,
        verdi?: number,
        relasjonsId?: number
    ) =>
    (dispatch: Dispatch, getState: () => RootState, integration: Integration) => {
        // Don't add type to search if it is already added. Add-buttons are disabled already,
        // but this is a failsafe which also catches attempts to add from search field.
        if (
            stateSelectedRoadObjectTypes(getState()).find(
                votState => typeIdOf(votState) === vegobjekttypeRaw.id
            )
        ) {
            return;
        }

        const egenskapstype = egenkapsId
            ? vegobjekttypeRaw.egenskapstypeById(egenkapsId)
            : undefined;
        const filters = egenskapstype
            ? [
                  new Egenskapsfilter(
                      egenskapstype,
                      verdi ? OperatorE.EQ : OperatorE.HAS_VALUE,
                      verdi ? egenskapstype.verdiById(verdi) : ''
                  ),
              ]
            : [];
        const initialAdvancedFilter = migrateLegacyFiltersToAdvancedFilter(filters);
        const advancedFilterWithRelasjon = relasjonsId
            ? appendAdvancedFilterNode(
                  initialAdvancedFilter,
                  null,
                  createAdvancedFilterRelasjonNode(relasjonsId)
              )
            : initialAdvancedFilter;
        const votState = new VegobjekttypeState(
            vegobjekttypeRaw.id,
            resolveInitialVegobjekttypeColor(
                vegobjekttypeRaw.id,
                integration.colorHandler
            ),
            undefined,
            undefined,
            undefined,
            filters
        ).withAdvancedFilters(advancedFilterWithRelasjon, advancedFilterWithRelasjon);
        const addVegobjekttypeAction: VOAddAction = {
            type: ActionType.VEGOBJEKTER_TYPE_ADDED,
            vegobjekttype: votState,
        };
        dispatch(addVegobjekttypeAction);
        dispatch(fetchVegobjekter(votState));

        // Change selected object color if new vegobjektttype matches selection's type.
        const mapSelection = stateValgt(getState());
        const selectedIsOfType =
            mapSelection.singleVegobjekt()?.metadata?.type?.id === vegobjekttypeRaw.id;
        if (selectedIsOfType) {
            dispatch(updateSelectedColor(resolveColor(votState.color!)));
        }
    };

export type toggleVegobjekttypeVisibilityT = (
    votState: VegobjekttypeState
) => (dispatch: Dispatch) => void;
export const toggleVegobjekttypeVisibility: toggleVegobjekttypeVisibilityT =
    (votState: VegobjekttypeState) => (dispatch: Dispatch) => {
        const toggleVOTAction: VOToggleAction = {
            type: ActionType.VEGOBJEKTER_TYPE_TOGGLED,
            votTypeId: typeIdOf(votState),
        };
        dispatch(toggleVOTAction);
    };

// Applies a color change for a vegobjekttype and, if the object is currently selected on the
// map, keeps the selection's displayed color in sync. Does not touch the ColorHandler or
// localStorage - callers are responsible for that bookkeeping before calling this.
const applyVegobjekttypeColor =
    (votState: VegobjekttypeState, color: ColorIndex, colorIsManual: boolean) =>
    (dispatch: Dispatch, getState: () => RootState) => {
        const colorChangedAction: VOColorChangedAction = {
            type: ActionType.VEGOBJEKTER_TYPE_COLOR_CHANGED,
            votTypeId: typeIdOf(votState),
            color,
            colorIsManual,
        };
        dispatch(colorChangedAction);

        const mapSelection = stateValgt(getState());
        const selectedIsOfType =
            mapSelection.singleVegobjekt()?.metadata?.type?.id === typeIdOf(votState);
        if (selectedIsOfType) {
            dispatch(updateSelectedColor(resolveColor(color)));
        }
    };

const rememberCustomColor = (color: ColorIndex) => {
    if (color.customColor) {
        addRecentCustomColor(color.customColor);
    }
};

const replaceDisplayedColor = (
    integration: Integration,
    previousColor: ColorIndex | null,
    nextColor: ColorIndex,
    rememberRecentCustomColor = true
) => {
    integration.colorHandler.decrementColor(previousColor);
    integration.colorHandler.reserveColor(nextColor);
    if (rememberRecentCustomColor) {
        rememberCustomColor(nextColor);
    }
};

const releaseDisplayedColor = (integration: Integration, color: ColorIndex | null) => {
    integration.colorHandler.decrementColor(color);
};

export type setVegobjekttypeColorT = (
    votState: VegobjekttypeState,
    color: ColorIndex,
    saveAsDefault?: boolean
) => (dispatch: Dispatch, getState: GetState, integration: Integration) => void;
// Manually set the color for a vegobjekttype, either just for this session or - if
// `saveAsDefault` is set - persisted so it is reapplied whenever the type is added again.
export const setVegobjekttypeColor: setVegobjekttypeColorT =
    (votState: VegobjekttypeState, color: ColorIndex, saveAsDefault = false) =>
    (dispatch: Dispatch, getState: () => RootState, integration: Integration) => {
        replaceDisplayedColor(integration, votState.color, color);
        if (saveAsDefault) {
            setDefaultVegobjekttypeColor(typeIdOf(votState), color);
        }
        dispatch(applyVegobjekttypeColor(votState, color, true));
    };

export type resetVegobjekttypeColorT = (
    votState: VegobjekttypeState
) => (dispatch: Dispatch, getState: GetState, integration: Integration) => void;
// Resets a vegobjekttype's color back to the automatic allocator and removes any saved
// default color for this type.
export const resetVegobjekttypeColor: resetVegobjekttypeColorT =
    (votState: VegobjekttypeState) =>
    (dispatch: Dispatch, getState: () => RootState, integration: Integration) => {
        releaseDisplayedColor(integration, votState.color);
        clearDefaultVegobjekttypeColor(typeIdOf(votState));
        const nextColor = integration.colorHandler.getNextColorIndex();
        dispatch(applyVegobjekttypeColor(votState, nextColor, false));
    };

export type setVegobjekttypeCategoryColorT = (
    votState: VegobjekttypeState,
    categoryState: VegobjekttypeCategoryState,
    color: ColorIndex,
    saveAsDefault?: boolean
) => (dispatch: Dispatch, getState: GetState, integration: Integration) => void;
// Manually override the color of a single category within a categorized vegobjekttype.
export const setVegobjekttypeCategoryColor: setVegobjekttypeCategoryColorT =
    (
        votState: VegobjekttypeState,
        categoryState: VegobjekttypeCategoryState,
        color: ColorIndex,
        saveAsDefault = false
    ) =>
    (dispatch: Dispatch, _getState: GetState, integration: Integration) => {
        replaceDisplayedColor(integration, categoryState.color, color);
        if (saveAsDefault) {
            setDefaultCategoryColor(typeIdOf(votState), categoryState.filters, color);
        }
        const action: VOCategoryColorChangedAction = {
            type: ActionType.VEGOBJEKTER_CATEGORY_COLOR_CHANGED,
            votTypeId: typeIdOf(votState),
            categoryId: categoryState.id,
            color,
            colorIsManual: true,
        };
        dispatch(action);
    };

export type resetVegobjekttypeCategoryColorT = (
    votState: VegobjekttypeState,
    categoryState: VegobjekttypeCategoryState
) => (dispatch: Dispatch, getState: GetState, integration: Integration) => void;
// Resets a single category's color override back to an automatically allocated color.
export const resetVegobjekttypeCategoryColor: resetVegobjekttypeCategoryColorT =
    (votState: VegobjekttypeState, categoryState: VegobjekttypeCategoryState) =>
    (dispatch: Dispatch, _getState: GetState, integration: Integration) => {
        releaseDisplayedColor(integration, categoryState.color);
        clearDefaultCategoryColor(typeIdOf(votState), categoryState.filters);
        const nextColor = integration.colorHandler.getNextColorIndex();
        const action: VOCategoryColorChangedAction = {
            type: ActionType.VEGOBJEKTER_CATEGORY_COLOR_CHANGED,
            votTypeId: typeIdOf(votState),
            categoryId: categoryState.id,
            color: nextColor,
            colorIsManual: false,
        };
        dispatch(action);
    };

export type setAllCategoryColorsT = (
    votState: VegobjekttypeState,
    color: ColorIndex,
    saveAsDefault?: boolean
) => (dispatch: Dispatch, getState: GetState, integration: Integration) => void;
// Quick action: apply the same color to every category of a vegobjekttype.
export const setAllCategoryColors: setAllCategoryColorsT =
    (votState: VegobjekttypeState, color: ColorIndex, saveAsDefault = false) =>
    (dispatch: Dispatch, _getState: GetState, integration: Integration) => {
        // Reserve once per category to match the per-category decrement below - otherwise the
        // shared color's usage count ends up lower than the number of categories actually
        // displaying it, and later individual resets can free the slot while other categories
        // are still using it, causing them to collide on the same "new" automatic color.
        votState.categoryStates.forEach(categoryState => {
            replaceDisplayedColor(integration, categoryState.color, color, false);
        });
        rememberCustomColor(color);
        if (saveAsDefault) {
            votState.categoryStates.forEach(categoryState =>
                setDefaultCategoryColor(typeIdOf(votState), categoryState.filters, color)
            );
        }
        const action: VOAllCategoryColorsChangedAction = {
            type: ActionType.VEGOBJEKTER_ALL_CATEGORY_COLORS_CHANGED,
            votTypeId: typeIdOf(votState),
            categoryStates: votState.categoryStates.map(c => c.withColor(color, true)),
        };
        dispatch(action);
    };

export type resetAllCategoryColorsT = (
    votState: VegobjekttypeState
) => (dispatch: Dispatch, getState: GetState, integration: Integration) => void;
// Quick action: reset every category of a vegobjekttype back to distinct automatically
// allocated colors.
export const resetAllCategoryColors: resetAllCategoryColorsT =
    (votState: VegobjekttypeState) =>
    (dispatch: Dispatch, _getState: GetState, integration: Integration) => {
        clearDefaultCategoryColors(typeIdOf(votState));
        const categoryStates = votState.categoryStates.map(categoryState => {
            releaseDisplayedColor(integration, categoryState.color);
            return categoryState.withColor(
                integration.colorHandler.getNextColorIndex(),
                false
            );
        });
        const action: VOAllCategoryColorsChangedAction = {
            type: ActionType.VEGOBJEKTER_ALL_CATEGORY_COLORS_CHANGED,
            votTypeId: typeIdOf(votState),
            categoryStates,
        };
        dispatch(action);
    };

const releaseCategoryColors = (
    votState: VegobjekttypeState,
    integration: Integration
) => {
    votState.categoryStates.forEach(categoryState => {
        releaseDisplayedColor(integration, categoryState.color);
    });
};

export type removeVegobjekttypeT = (
    votState: VegobjekttypeState
) => (dispatch: Dispatch, getState: GetState, integration: Integration) => void;
export const removeVegobjekttype: removeVegobjekttypeT =
    (votState: VegobjekttypeState) =>
    (dispatch: Dispatch, getState: () => RootState, integration: Integration) => {
        const removeVOTAction: VORemoveAction = {
            type: ActionType.VEGOBJEKTER_TYPE_REMOVED,
            votTypeId: typeIdOf(votState),
        };
        dispatch(removeVOTAction);
        releaseDisplayedColor(integration, votState.color);
        releaseCategoryColors(votState, integration);

        // Change selected object color if old vegobjekttype matches selection's type.
        const mapSelection = stateValgt(getState());
        const selectedIsOfType =
            mapSelection.singleVegobjekt()?.metadata?.type?.id === typeIdOf(votState);
        if (selectedIsOfType) {
            dispatch(updateSelectedColor(defaultColor));
        }
    };

export type addCategorizationToVegobjekttypeT = (
    votState: VegobjekttypeState
) => (dispatch: Dispatch) => void;
export const addCategorizationToVegobjekttype: addCategorizationToVegobjekttypeT =
    (votState: VegobjekttypeState) => (dispatch: Dispatch) => {
        const addCatToVOTAction: VOAddCategoryAction = {
            type: ActionType.VEGOBJEKTER_CATEGORY_ADDED,
            votTypeId: typeIdOf(votState),
            egenskapstype: votState.category!,
        };
        dispatch(addCatToVOTAction);
    };

export type isCategorizingT = (categorizing: boolean) => (dispatch: Dispatch) => void;
export const isCategorizing: isCategorizingT =
    (categorizing: boolean) => (dispatch: Dispatch) => {
        const isAddingCatToVOTAction: IsAddingCatToVOAction = {
            type: ActionType.IS_ADDING_CAT_TO_VOT,
            categorizing,
        };
        dispatch(isAddingCatToVOTAction);
    };

export type setVOTCategoryT = (
    votState: VegobjekttypeState,
    egenskapstype: Egenskapstype,
    interval?: number[] | null,
    absoluteIntervals?: boolean
) => (dispatch: Dispatch) => void;
export const setVOTCategory: setVOTCategoryT =
    (
        votState: VegobjekttypeState,
        egenskapstype: Egenskapstype,
        interval: number[] | null = null,
        absoluteIntervals = false
    ) =>
    (dispatch: Dispatch) => {
        const setVOTCatAction: VOSetCategoryAction = {
            type: ActionType.VEGOBJEKTER_CATEGORY_UPDATED,
            votTypeId: typeIdOf(votState),
            egenskapstype,
            interval: interval!,
            absoluteIntervals,
        };
        dispatch(setVOTCatAction);
        dispatch(
            fetchVegobjekter(
                votState
                    .withCategory(egenskapstype)
                    .withInterval(interval!, absoluteIntervals)
            )
        );
    };

export type toggleCategoryT = (
    votTypeId: number,
    votCategoryState: VegobjekttypeCategoryState
) => (dispatch: Dispatch) => void;
export const toggleCategory: toggleCategoryT =
    (votTypeId: number, votCategoryState: VegobjekttypeCategoryState) =>
    (dispatch: Dispatch) => {
        const toggleCatAction: VOToggleCategoryAction = {
            type: ActionType.VEGOBJEKTER_CATEGORY_TOGGLED,
            votTypeId,
            votCategoryState,
        };
        dispatch(toggleCatAction);
    };

export type removeVOTCategoryT = (
    votState: VegobjekttypeState
) => (dispatch: Dispatch, getState: GetState, integration: Integration) => void;
export const removeVOTCategory: removeVOTCategoryT =
    (votState: VegobjekttypeState) =>
    (dispatch: Dispatch, _getState: GetState, integration: Integration) => {
        const removeVOTCategoryAction: VORemoveCategoryAction = {
            type: ActionType.VEGOBJEKTER_CATEGORY_REMOVED,
            votTypeId: typeIdOf(votState),
        };
        releaseCategoryColors(votState, integration);
        dispatch(removeVOTCategoryAction);

        // If the category was not valid (no categorisation was specified) there is no point in executing the search.
        if (votState.categoryIsValid()) {
            dispatch(
                fetchVegobjekter(
                    votState.override({ category: null }).withCategoryStates([])
                )
            );
        }
    };

export type replaceAdvancedFilterDraftT = (
    votState: VegobjekttypeState,
    draft: AdvancedFilterGroupNode | null
) => (dispatch: Dispatch) => void;
export const replaceAdvancedFilterDraft: replaceAdvancedFilterDraftT =
    (votState: VegobjekttypeState, draft: AdvancedFilterGroupNode | null) =>
    (dispatch: Dispatch) => {
        const normalizedDraft = normalizeAdvancedFilterRoot(draft);
        const replaceDraftAction: VOUpdateAdvancedFilterDraftAction = {
            type: ActionType.VEGOBJEKTER_ADVANCED_FILTER_DRAFT_UPDATED,
            votTypeId: typeIdOf(votState),
            draft: normalizedDraft,
        };
        dispatch(replaceDraftAction);

        const validation = validateAdvancedFilter(normalizedDraft);
        if (!validation.valid) {
            return;
        }

        const applyAction: VOUpdateAdvancedFilterAppliedAction = {
            type: ActionType.VEGOBJEKTER_ADVANCED_FILTER_APPLIED_UPDATED,
            votTypeId: typeIdOf(votState),
            applied: normalizedDraft,
        };
        dispatch(applyAction);
        dispatch(fetchVegobjekter(votState));
    };

export type clearAdvancedFilterT = (
    votState: VegobjekttypeState
) => (dispatch: Dispatch) => void;
export const clearAdvancedFilter: clearAdvancedFilterT =
    (votState: VegobjekttypeState) => (dispatch: Dispatch) => {
        if (
            votState.filters.length === 0 &&
            votState.advancedFilterDraft === null &&
            votState.advancedFilterApplied === null
        ) {
            return;
        }

        const clearAction: VOClearAdvancedFilterAction = {
            type: ActionType.VEGOBJEKTER_ADVANCED_FILTER_CLEARED,
            votTypeId: typeIdOf(votState),
        };
        dispatch(clearAction);
        dispatch(fetchVegobjekter(votState));
    };
